import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { updateCollectionOnCollectionsList } from '../../actions/Collections';

import {
  APIgetCollectionsSummary,
  APIsyncCollection,
  APIunsyncCollection,
} from '../../api/Collections';

import ProcessingSpinner from '../ProcessingSpinner';
import ErrorBoundary from '../ErrorBoundary';

import { addFlashMessage } from '../../helpers/FlashMessages';

const SyncModal = ({
  collection,
  updateCollection,
  handleClose,
}) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(!collection.synced);
  const [summary, setSummary] = useState({ files: 0, collections: 0 });
  const [error, setError] = useState(false);

  const getButtonText = () => {
    let text = '';

    if (collection.synced) {
      text = intl.formatMessage({ defaultMessage: 'Unsync' });
    } else {
      text = intl.formatMessage({ defaultMessage: 'Sync' });
    }

    if (loading) {
      text = intl.formatMessage({ defaultMessage: 'Processing...' });
    }

    return text;
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      let response;
      if (collection.synced) {
        response = await APIunsyncCollection(collection.uuid);
      } else {
        response = await APIsyncCollection(collection.uuid);
      }

      if (response.status === 200) {
        setSummary(response.data);
      }
    } catch (_) {
      setError(true);
    } finally {
      updateCollection(collection);
      addFlashMessage({
        type: 'success',
        text: collection.synced ?
          intl.formatMessage({ defaultMessage: 'You unsynced the Collection' }) :
          intl.formatMessage({ defaultMessage: 'You synced the Collection' }),
      });
      setLoading(false);
      handleClose();
    }
  };

  useEffect(() => {
    if (collection.synced) {
      return;
    }

    const fetchCollectionsSummary = async () => {
      try {
        const response = await APIgetCollectionsSummary({
          collectionUuidList: [collection.uuid],
        });

        if (response.status === 200) {
          setSummary(response.data);
        }
      } catch (_) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionsSummary();
  }, [collection]);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header">
        {intl.formatMessage({ defaultMessage: 'Are you sure?' })}
      </ModalHeader>
      <ModalBody>
        <p>
          {collection.synced ? (
            intl.formatMessage({ defaultMessage: 'Are you sure you want to unsync this Collection?' })
          ) : (
            intl.formatMessage({ defaultMessage: 'Are you sure you want to sync this Collection?' })
          )}
        </p>
        {(summary?.files > 0 || summary?.collections > 0) && (
          <span className="summary">
            {intl.formatMessage({
              defaultMessage: `<strong>{collectionsCount}</strong>
                {collectionsCount, plural, one {Collection} other {Collections}} and
                <strong>{filesCount}</strong> {filesCount, plural, one {file} other {files}}
                will be synced`,
            }, {
              collectionsCount: summary.collections,
              filesCount: summary.files,
              strong: (msg) => <strong>{msg}</strong>,
            })}
          </span>
        )}
        {error && (
          <p className="error">
            {intl.formatMessage({
              defaultMessage: 'Something went wrong. Please try again or contact us.',
            })}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={loading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={loading}
            onClick={handleConfirm}
          >
            <ProcessingSpinner show={loading} />
            {getButtonText()}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  settings: state.App.settings,
});

const mapDispatchToProps = dispatch => ({
  updateCollection: (collection) => {
    dispatch(updateCollectionOnCollectionsList({ ...collection, synced: !collection.synced }));
  },
});

SyncModal.propTypes = {
  collection: PropTypes.object.isRequired,
  updateCollection: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(SyncModal);
